import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const About: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Fonction pour obtenir l'image correspondante à une technologie
  const getTechImage = (techName: string) => {
    const imageMap: { [key: string]: string } = {
      ReactJS: "/img/reactjs.png",
      TypeScript: "/img/typescript.png",
      "Node.js": "/img/nodejs.png",
      TailwindCSS: "/img/tailwindcss.png",
    };

    return imageMap[techName] || null;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut" as const,
      },
    },
  };

  const technologies = [
    { name: "ReactJS", icon: "⚛️" },
    { name: "TypeScript", icon: "📘" },
    { name: "Node.js", icon: "🟢" },
    { name: "TailwindCSS", icon: "🐍" },
  ];

  const stats = [
    { label: "Projets réalisés", value: "8+" },
    { label: "Technologies maîtrisées", value: "20+" },
    { label: "Années d'expérience", value: "3+" },
  ];

  return (
    <section
      id="about"
      className="py-20 bg-gradient-to-br from-secondary-50 via-white to-blue-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-blue-900/20 relative overflow-hidden"
    >
      {/* Background decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-10 right-10 w-32 h-32 bg-primary-200 dark:bg-primary-600 rounded-full opacity-20 animate-float"></div>
        <div
          className="absolute bottom-10 left-10 w-40 h-40 bg-blue-200 dark:bg-blue-600 rounded-full opacity-20 animate-float"
          style={{ animationDelay: "2s" }}
        ></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 bg-gradient-to-r from-gray-900 via-primary-600 to-blue-600 dark:from-white dark:via-primary-400 dark:to-blue-400 bg-clip-text text-transparent">
              À propos
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-400 dark:to-blue-400 mx-auto rounded-full mb-8"></div>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Découvrez mon parcours et ma passion pour le développement web
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - About Text */}
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="bg-white/80 dark:bg-secondary-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-soft dark:shadow-dark-soft border border-white/20 dark:border-secondary-700/20">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Mon Histoire
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
                  Passionné par les technologies depuis toujours, j'ai découvert
                  le développement web au fil de mes études - une vraie
                  révélation ! Mon parcours m'a amené à Paris, où j'ai consolidé
                  mes compétences à travers plusieurs diplômes spécialisés. En
                  alternance chez Splash360, j'ai pu mettre en pratique mes
                  connaissances sur des projets concrets et développer une
                  approche professionnelle du développement.
                </p>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  Spécialisé dans les technologies modernes, je maîtrise React,
                  TypeScript, Node.js et NestJS. Mon expertise couvre l'ensemble
                  du cycle de développement : du frontend avec des frameworks
                  comme ShadCN, au backend robuste, en passant par le
                  déploiement (Docker, VPS). J'aime créer des solutions
                  complètes et performantes.
                </p>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                {stats.map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                    className="bg-white/60 dark:bg-secondary-800/60 backdrop-blur-sm rounded-xl p-4 text-center border border-white/20 dark:border-secondary-700/20"
                  >
                    <div className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {stat.label}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Column - Technologies */}
            <motion.div variants={itemVariants} className="space-y-6">
              <div className="bg-white/80 dark:bg-secondary-800/80 backdrop-blur-sm rounded-2xl p-8 shadow-soft dark:shadow-dark-soft border border-white/20 dark:border-secondary-700/20">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                  Technologies Principales
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {technologies.map((tech, index) => {
                    const techImage = getTechImage(tech.name);

                    return (
                      <motion.div
                        key={tech.name}
                        variants={itemVariants}
                        whileHover={{ scale: 1.05, y: -2 }}
                        className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-secondary-700/50 border border-gray-200 dark:border-secondary-600/50"
                      >
                        {techImage ? (
                          <img
                            src={techImage}
                            alt={tech.name}
                            className="w-8 h-8 object-contain"
                          />
                        ) : (
                          <span className="text-2xl">{tech.icon}</span>
                        )}
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {tech.name}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Additional Info */}
              <div className="bg-gradient-to-r from-primary-600 to-blue-600 dark:from-primary-500 dark:to-blue-500 rounded-2xl p-6 text-white">
                <h4 className="text-xl font-bold mb-3">
                  En constante évolution
                </h4>
                <p className="text-primary-100 dark:text-primary-200">
                  Apprendre et m’améliorer font partie de mon quotidien de
                  développeur. Aujourd’hui diplômé et pleinement opérationnel,
                  je suis prêt à rejoindre une équipe ambitieuse, continuer à
                  progresser et contribuer à des projets concrets avec des
                  solutions web solides et modernes.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
